import React, { useState, useEffect } from 'react';
import {
  BiPlus,
  BiTrash,
  BiX,
  BiCheck,
  BiSearch,
  BiLink,
  BiInfoCircle
} from 'react-icons/bi';
import './CustomFonts.css';
import { loadFontInHead } from '../../utils/fontHelper';
import { buildApiUrl } from '../../config/api';

const CustomFonts = ({ theme, projects }) => {
  const [fonts, setFonts] = useState([]);
  const [userFonts, setUserFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newFont, setNewFont] = useState({
    name: '',
    family: '',
    category: 'sans-serif',
    variants: ['regular'],
    googleUrl: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFonts, setProjectFonts] = useState([]);
  const [isManageProjectFontsModal, setIsManageProjectFontsModal] = useState(false);
  const [projectFontsLoading, setProjectFontsLoading] = useState(false);
  const [addingFontToProject, setAddingFontToProject] = useState(false);
  const [removingFontFromProject, setRemovingFontId] = useState(null);
  const [settingDefaultFontId, setSettingDefaultFontId] = useState(null);
  
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Fetch all fonts on component mount
  useEffect(() => {
    fetchFonts();
    if (userData.id) {
      fetchUserFonts(userData.id);
    }
  }, [userData.id, currentPage, selectedCategory, searchQuery]);
  
  // Fetch fonts function
  const fetchFonts = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 12,
        category: selectedCategory,
        query: searchQuery
      });
      
      const response = await fetch(buildApiUrl(`api/fonts?${queryParams}`));
      const data = await response.json();
      
      if (response.ok) {
        setFonts(data.fonts);
        setTotalPages(data.pagination.pages);
        setError(null);
        
        // Load Google Fonts for preview
        loadGoogleFonts(data.fonts);
      } else {
        setError(data.error || 'Failed to fetch fonts');
      }
    } catch (error) {
      console.error('Error fetching fonts:', error);
      setError('Network error: Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch user fonts
  const fetchUserFonts = async (userId) => {
    try {
      const response = await fetch(buildApiUrl(`api/fonts/user/${userId}`));
      const data = await response.json();
      
      if (response.ok) {
        setUserFonts(data.fonts);
      } else {
        console.error('Error fetching user fonts:', data.error);
      }
    } catch (error) {
      console.error('Error fetching user fonts:', error);
    }
  };
  
  // Fetch project fonts
  const fetchProjectFonts = async (siteId) => {
    setProjectFontsLoading(true);
    try {
      const response = await fetch(buildApiUrl(`api/sites/${siteId}/fonts`));
      const data = await response.json();
      
      if (response.ok) {
        // Ensure projectFonts is properly formatted for rendering
        if (data.fonts && Array.isArray(data.fonts)) {
          setProjectFonts(data.fonts);
        } else {
          // If the API returns an unexpected format, set empty array
          setProjectFonts([]);
          console.error('Unexpected format for project fonts:', data);
        }
      } else {
        console.error('Error fetching project fonts:', data.error);
        setError(data.error || 'Failed to fetch project fonts');
      }
    } catch (error) {
      console.error('Error fetching project fonts:', error);
      setError('Network error: Unable to connect to server');
    } finally {
      setProjectFontsLoading(false);
    }
  };
  
  // Load Google Fonts for preview
  const loadGoogleFonts = (fontsToLoad) => {
    fontsToLoad.forEach(font => {
      if (font.googleUrl) {
        loadFontInHead(font, 'data-preview-font');
      }
    });
  };
  
  // Add font to database
  const handleAddFont = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newFont.name.trim() || !newFont.family.trim() || !newFont.googleUrl.trim()) {
      setError('Font name, family and Google URL are required');
      return;
    }
    
    // Ensure variants are in the correct format
    const processedVariants = Array.isArray(newFont.variants) && newFont.variants.length > 0 
      ? newFont.variants 
      : ['regular'];
    
    try {
      // Ensure we have a valid userId
      if (!userData.id) {
        setError('You must be logged in to add fonts');
        return;
      }
      
      const response = await fetch(buildApiUrl('api/fonts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newFont.name.trim(),
          family: newFont.family.trim(),
          category: newFont.category,
          variants: processedVariants,
          googleUrl: newFont.googleUrl.trim(),
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Add new font to list and reset form
        if (data.font) {
          setFonts(prevFonts => [data.font, ...prevFonts]);
          setUserFonts(prevUserFonts => [data.font, ...prevUserFonts]);
          
          // Reset form
          setNewFont({
            name: '',
            family: '',
            category: 'sans-serif',
            variants: ['regular'],
            googleUrl: ''
          });
          
          setIsAddModalOpen(false);
          setError(null);
          
          // Load the new font for preview
          loadGoogleFonts([data.font]);
        } else {
          setError('Font was added but returned in unexpected format');
        }
      } else {
        setError(data.error || 'Failed to add font');
      }
    } catch (error) {
      console.error('Error adding font:', error);
      setError('Network error: Unable to connect to server');
    }
  };
  
  // Delete font
  const handleDeleteFont = async (fontId) => {
    if (!window.confirm('Are you sure you want to delete this font?')) {
      return;
    }
    
    try {
      const response = await fetch(buildApiUrl(`api/fonts/${fontId}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Remove font from lists
        setFonts(fonts.filter(font => font._id !== fontId));
        setUserFonts(userFonts.filter(font => font._id !== fontId));
        setError(null);
      } else {
        setError(data.error || 'Failed to delete font');
      }
    } catch (error) {
      console.error('Error deleting font:', error);
      setError('Network error: Unable to connect to server');
    }
  };
  
  // Handle adding font to project
  const handleAddFontToProject = async (fontId, isDefault = false) => {
    if (!selectedProject || !selectedProject.siteId) {
      setError('No project selected');
      return;
    }

    setAddingFontToProject(true);
    try {
      const response = await fetch(buildApiUrl(`api/sites/${selectedProject.siteId}/fonts`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fontId,
          isDefault,
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Refresh project fonts
        fetchProjectFonts(selectedProject.siteId);
        
        // Add the font to the document head for immediate use
        if (data.site && data.site.fonts) {
          const newFont = data.site.fonts.find(f => f.font._id === fontId);
          if (newFont && newFont.font) {
            loadFontInHead(newFont.font, 'data-custom-font');
          }
        }
      } else {
        setError(data.error || 'Failed to add font to project');
      }
    } catch (error) {
      console.error('Error adding font to project:', error);
      setError('Network error: Unable to connect to server');
    } finally {
      setAddingFontToProject(false);
    }
  };
  
  // Handle removing font from project
  const handleRemoveFontFromProject = async (fontId) => {
    if (!selectedProject || !selectedProject.siteId) {
      setError('No project selected');
      return;
    }

    setRemovingFontId(fontId);
    try {
      // Get the font details before removal
      const fontToRemove = projectFonts.find(pf => pf.font && pf.font._id === fontId);
      
      const response = await fetch(buildApiUrl(`api/sites/${selectedProject.siteId}/fonts/${fontId}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Refresh project fonts
        fetchProjectFonts(selectedProject.siteId);
        
        // Check if this font is still used in any other project before removing from DOM
        // For simplicity, we'll just leave existing fonts in the DOM
        // In a more complex app, you could check if this font is used in other projects
      } else {
        setError(data.error || 'Failed to remove font from project');
      }
    } catch (error) {
      console.error('Error removing font from project:', error);
      setError('Network error: Unable to connect to server');
    } finally {
      setRemovingFontId(null);
    }
  };
  
  // Handle setting default font for project
  const handleSetDefaultFont = async (fontId) => {
    if (!selectedProject || !selectedProject.siteId) {
      setError('No project selected');
      return;
    }

    setSettingDefaultFontId(fontId);
    try {
      const response = await fetch(buildApiUrl(`api/sites/${selectedProject.siteId}/fonts/${fontId}/default`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.id
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Refresh project fonts
        fetchProjectFonts(selectedProject.siteId);
      } else {
        setError(data.error || 'Failed to set default font');
      }
    } catch (error) {
      console.error('Error setting default font:', error);
      setError('Network error: Unable to connect to server');
    } finally {
      setSettingDefaultFontId(null);
    }
  };
  
  // Open project fonts management modal
  const openManageProjectFonts = (project) => {
    setSelectedProject(project);
    fetchProjectFonts(project.siteId);
    setIsManageProjectFontsModal(true);
  };
  
  // Font is already added to the project
  const isFontAddedToProject = (fontId) => {
    if (!projectFonts || !Array.isArray(projectFonts) || projectFonts.length === 0) {
      return false;
    }
    return projectFonts.some(pf => pf.font && pf.font._id === fontId);
  };
  
  // Check if font is the default for the project
  const isDefaultFont = (fontId) => {
    if (!projectFonts || !Array.isArray(projectFonts) || projectFonts.length === 0) {
      return false;
    }
    return projectFonts.some(pf => pf.font && pf.font._id === fontId && pf.isDefault);
  };
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFont({
      ...newFont,
      [name]: value
    });
  };
  
  // Handle variants change
  const handleVariantsChange = (e) => {
    const variants = Array.from(e.target.selectedOptions, option => option.value);
    setNewFont({
      ...newFont,
      variants
    });
  };
  
  // Format font family for CSS
  const formatFontFamily = (family, category) => {
    return `"${family}", ${category || 'sans-serif'}`;
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Filter fonts by selected category and search query
  const filteredFonts = fonts.filter(font => {
    const matchesSearch = 
      font.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      font.family.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || font.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  return (
    <div className="custom-fonts-container">
      <div className="custom-fonts-header">
        <h1>Custom Fonts</h1>
        <div className="custom-fonts-toolbar">
          <div className="search-container">
            <BiSearch className="search-icon" />
            <input 
              type="text" 
              className="search-input"
              placeholder="Search fonts..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="category-filter">
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="serif">Serif</option>
              <option value="sans-serif">Sans Serif</option>
              <option value="display">Display</option>
              <option value="handwriting">Handwriting</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>
          
          <button className="add-font-btn" onClick={() => setIsAddModalOpen(true)}>
            <BiPlus /> Add New Font
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading fonts...</p>
        </div>
      ) : (
        <>
          <div className="fonts-grid">
            {filteredFonts.map(font => (
              <div key={font._id} className="font-card">
                <div className="font-card-header">
                  <h3>{font.name}</h3>
                  <span className="font-category">{font.category}</span>
                </div>
                
                <div className="font-info">
                  <p>Family: <strong>{font.family}</strong></p>
                  <p>Variants: <strong>{font.variants.join(', ')}</strong></p>
                  <p className="font-type">
                    {font.isSystem ? (
                      <span className="system-font-badge">System Font</span>
                    ) : (
                      <span className="custom-font-badge">Custom Font</span>
                    )}
                  </p>
                </div>
                
                <div className="font-card-actions">
                  <button className="font-link-btn" onClick={() => window.open(font.googleUrl, '_blank')}>
                    <BiLink /> Google Font
                  </button>
                  
                  {!font.isSystem && (
                    <button className="font-delete-btn" onClick={() => handleDeleteFont(font._id)}>
                      <BiTrash /> Delete
                    </button>
                  )}
                </div>
                
                <div className="font-project-actions">
                  <select 
                    className="project-select"
                    onChange={(e) => {
                      const projectId = e.target.value;
                      if (projectId) {
                        const selectedProject = projects.find(p => p.siteId === projectId);
                        openManageProjectFonts(selectedProject);
                      }
                    }}
                  >
                    <option value="">Add to project...</option>
                    {projects.map(project => (
                      <option key={project.siteId} value={project.siteId}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
          
          <div className="pagination">
            <button 
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
      
      {/* Add Font Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Font</h2>
              <button className="close-modal" onClick={() => setIsAddModalOpen(false)}>&times;</button>
            </div>
            
            <form onSubmit={handleAddFont}>
              <div className="form-group">
                <label htmlFor="name">Font Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newFont.name}
                  onChange={handleInputChange}
                  placeholder="E.g. Roboto"
                  required
                />
                <small>The display name for the font (e.g., "Roboto")</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="family">Font Family</label>
                <input
                  type="text"
                  id="family"
                  name="family"
                  value={newFont.family}
                  onChange={handleInputChange}
                  placeholder="E.g. Roboto"
                  required
                />
                <small>The CSS font-family name (e.g., "Roboto")</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={newFont.category}
                  onChange={handleInputChange}
                >
                  <option value="sans-serif">Sans Serif</option>
                  <option value="serif">Serif</option>
                  <option value="display">Display</option>
                  <option value="handwriting">Handwriting</option>
                  <option value="monospace">Monospace</option>
                </select>
                <small>The font category for fallback purposes</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="variants">Variants</label>
                <select
                  id="variants"
                  name="variants"
                  multiple
                  value={newFont.variants}
                  onChange={handleVariantsChange}
                >
                  <option value="100">Thin (100)</option>
                  <option value="200">Extra Light (200)</option>
                  <option value="300">Light (300)</option>
                  <option value="regular">Regular (400)</option>
                  <option value="500">Medium (500)</option>
                  <option value="600">Semi Bold (600)</option>
                  <option value="700">Bold (700)</option>
                  <option value="800">Extra Bold (800)</option>
                  <option value="900">Black (900)</option>
                  <option value="100italic">Thin Italic</option>
                  <option value="200italic">Extra Light Italic</option>
                  <option value="300italic">Light Italic</option>
                  <option value="italic">Regular Italic</option>
                  <option value="500italic">Medium Italic</option>
                  <option value="600italic">Semi Bold Italic</option>
                  <option value="700italic">Bold Italic</option>
                  <option value="800italic">Extra Bold Italic</option>
                  <option value="900italic">Black Italic</option>
                </select>
                <small>Hold Ctrl/Cmd to select multiple variants. At least one variant is required.</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="googleUrl">Google Fonts URL</label>
                <input
                  type="url"
                  id="googleUrl"
                  name="googleUrl"
                  value={newFont.googleUrl}
                  onChange={handleInputChange}
                  placeholder="https://fonts.googleapis.com/css2?family=..."
                  required
                />
                <small>
                  <BiInfoCircle /> Get this URL from <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer">fonts.google.com</a> by selecting a font and copying the "Standard" link URL
                </small>
                <div className="google-font-help">
                  <ol className="help-steps">
                    <li>Go to <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer">fonts.google.com</a></li>
                    <li>Select a font you like</li>
                    <li>Click "Select this style" for each variant you want</li>
                    <li>Click the "View selected families" button at the bottom</li>
                    <li>In the sidebar that opens, select the "@import" tab</li>
                    <li>Copy the URL from the import statement (https://fonts.googleapis.com/css2?family=...)</li>
                  </ol>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                >
                  Add Font
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Manage Project Fonts Modal */}
      {isManageProjectFontsModal && selectedProject && (
        <div className="modal-overlay" onClick={() => setIsManageProjectFontsModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Fonts for {selectedProject.name}</h2>
              <button className="close-modal" onClick={() => setIsManageProjectFontsModal(false)}>&times;</button>
            </div>
            
            <div className="project-fonts-container">
              <h3>Current Project Fonts</h3>
              
              {projectFontsLoading ? (
                <div className="loading-indicator">
                  <div className="spinner-small"></div>
                  <p>Loading project fonts...</p>
                </div>
              ) : projectFonts.length === 0 ? (
                <div className="no-fonts-message">
                  <p>No fonts added to this project yet.</p>
                </div>
              ) : (
                <div className="project-fonts-list">
                  {projectFonts.map(pf => (
                    <div key={pf.font._id} className="project-font-item">
                      <div className="project-font-info">
                        <h4>{pf.font.name}</h4>
                        {pf.isDefault && <span className="default-badge">Default</span>}
                      </div>
                      
                      <div className="project-font-actions">
                        {!pf.isDefault && (
                          <button 
                            className="set-default-btn"
                            onClick={() => handleSetDefaultFont(pf.font._id)}
                            disabled={settingDefaultFontId === pf.font._id}
                          >
                            {settingDefaultFontId === pf.font._id ? (
                              <><span className="button-spinner"></span> Setting...</>
                            ) : (
                              <><BiCheck /> Set Default</>
                            )}
                          </button>
                        )}
                        
                        <button 
                          className="remove-font-btn"
                          onClick={() => handleRemoveFontFromProject(pf.font._id)}
                          disabled={removingFontFromProject === pf.font._id}
                        >
                          {removingFontFromProject === pf.font._id ? (
                            <><span className="button-spinner"></span> Removing...</>
                          ) : (
                            <><BiX /> Remove</>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <h3>Available Fonts</h3>
              
              <div className="available-fonts-list">
                {fonts.filter(font => !isFontAddedToProject(font._id)).map(font => (
                  <div key={font._id} className="available-font-item">
                    <div className="available-font-info">
                      <h4>{font.name}</h4>
                    </div>
                    
                    <div className="available-font-actions">
                      <button 
                        className="add-to-project-btn"
                        onClick={() => handleAddFontToProject(font._id, projectFonts.length === 0)}
                        disabled={addingFontToProject}
                      >
                        {addingFontToProject ? (
                          <><span className="button-spinner"></span> Adding...</>
                        ) : (
                          <><BiPlus /> Add to Project</>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="close-btn"
                onClick={() => setIsManageProjectFontsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFonts; 